import React, { useEffect, useState } from 'react'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { getIn } from 'formik'
import {
  isMobile,
  hideResponsiveButton,
  showResponsiveButton,
} from '../helpers'
import { useRef } from 'react'

const { Control, Group, Label } = Form

export default function FormFieldCounter({
  field: { value = '', ...field } = {},
  form: { touched = {}, errors = {}, setFieldValue } = {},
  icon,
  groupProps = {},
  handleChange,
  label,
  hideToolTip = false,
  controlId,
  disabled = false,
  groupClassName,
  minValue = 0,
  maxValue,
  ...props
}) {
  const getClampedCount = next => {
    const normalized = Math.max(Number(next) || 0, minValue)
    return maxValue != null ? Math.min(normalized, maxValue) : normalized
  }

  const [count, setCount] = useState(() => getClampedCount(value ?? minValue))
  const showError = !!getIn(touched, field.name) && !!getIn(errors, field.name)
  const isMobileBrowser = isMobile()
  const fieldRef = useRef(null)
  const windowHeight = window.innerHeight
  var isFocused = false
  var isScrolled = false

  const commitCount = nextCount => {
    const clamped = getClampedCount(nextCount)
    if (clamped === count) return
    setCount(clamped)
    setFieldValue(field.name, clamped)
  }

  useEffect(() => {
    if (value === '' || value == null) return
    const numericValue = Number(value)
    if (Number.isNaN(numericValue)) return
    const clamped = getClampedCount(numericValue)
    if (clamped !== count) setCount(clamped)
  }, [value, minValue, maxValue])

  // Clamp when max capacity shrinks (e.g. another passenger type was increased)
  useEffect(() => {
    if (maxValue != null && count > maxValue) {
      setCount(maxValue)
      setFieldValue(field.name, maxValue)
    }
  }, [maxValue, count, field.name, setFieldValue])

  const handleMinus = () => {
    commitCount(count - 1)
  }

  const handlePlus = () => {
    if (maxValue != null && count >= maxValue) return
    commitCount(count + 1)
  }

  const renderTooltipText = label => {
    switch (label) {
      case 'Baby seat':
        return (
          <>
            <span style={{ display: 'block' }}>For children weighing</span>
            <span style={{ display: 'block' }}>under 20 lbs/9 kg</span>
            <span style={{ display: 'block' }}>(0-1 year old)</span>
          </>
        )
      case 'Child seat':
        return (
          <>
            <span style={{ display: 'block' }}>For children weighing</span>
            <span style={{ display: 'block' }}>under 20-40 lbs/9-18 kg</span>
            <span style={{ display: 'block' }}>(1-3 years old)</span>
          </>
        )
      case 'Booster seat':
        return (
          <>
            <span style={{ display: 'block' }}>For children weighing</span>
            <span style={{ display: 'block' }}>under 40-100 lbs/18-45 kg</span>
            <span style={{ display: 'block' }}>(3-11 years old)</span>
          </>
        )
      default:
        return
    }
  }

  var timer = null

  if (isMobileBrowser) {
    if (timer) {
      clearInterval(timer)
    }
    timer = setInterval(() => {
      var allScrollables = document.getElementsByClassName(
        'scrollable-page-content'
      )

      var elScrollable = null

      if (allScrollables.length > 0) {
        elScrollable = allScrollables[allScrollables.length - 1]
      }

      if (!fieldRef || !fieldRef.current || !elScrollable) {
        clearInterval(timer)
        return
      }

      if (!isFocused) {
        return
      }

      if (windowHeight > window.innerHeight && !isScrolled) {
        setTimeout(() => {
          var bottom_gap =
            elScrollable.offsetHeight -
            fieldRef.current.getBoundingClientRect().bottom

          if (bottom_gap < 150) {
            const addScroll = 150 - bottom_gap
            elScrollable.scrollTop = elScrollable.scrollTop + addScroll
          }
        }, 200)
        isScrolled = true
      }
    }, 100)
  }

  return (
    <Group
      className={groupClassName || ''}
      controlId={controlId || field.name}
      style={
        isMobileBrowser
          ? {
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }
          : {}
      }
    >
      <div className='form-label-div'>
        {icon && (
          <img
            src={icon}
            alt='Icon'
            className='form-label-icon'
            style={{ paddingBottom: '2px' }}
          />
        )}
        {!!label && <Label className='form-label-counter'>{label}</Label>}
        {!hideToolTip ? (
          <OverlayTrigger
            delay={{
              show: 100,
              hide: 100,
            }}
            placement='right-start'
            overlay={
              <Tooltip
                id='button-tooltip'
                className='clientInfoTooltip-childSeat'
              >
                {renderTooltipText(label)}
              </Tooltip>
            }
          >
            <img
              className='form-counter-tooltip'
              src='/images/icons/icon-information.png'
              alt=''
            />
          </OverlayTrigger>
        ) : (
          <></>
        )}
      </div>

      <div className='form-field-counter'>
        <div className='form-field-minus' onClick={handleMinus}>
          <img src='/images/icons/icon-minus.png' alt='' />
        </div>
        <Control
          ref={fieldRef}
          type='text'
          value={count}
          readOnly
          {...props}
          isInvalid={showError}
          onChange={() => {}}
          disabled={disabled}
          className='form-field'
        />
        <div className='form-field-plus' onClick={handlePlus}>
          <img src='/images/icons/icon-plus.png' alt='' />
        </div>
      </div>

      {!!showError && (
        <Control.Feedback type='invalid'>
          {getIn(errors, field.name)}
        </Control.Feedback>
      )}
    </Group>
  )
}
